import "./App.css";
import {
  Suspense,
  lazy,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import PublicRoutes from "./components/common/PublicRoutes/PublicRoutes";
import PrivateRoutes from "./components/common/PrivateRoutes/PrivateRoutes";
// import "react-datepicker/dist/react-datepicker.css";
import { setupInterceptors } from "./api/axiosConfig";
import { useLoader } from "./hooks/useLoader";
import { useToast } from "./hooks/useToast";
import { useAuth, useLogger } from "./hooks";
import { LogLevel } from "./enums";
import LoadingFallback from "./components/common/LoadingFallback/LoadingFallback";
import Login from "./pages/Login/Login";
import RouteTitle from "./components/RouteTitle";
import Footer from "./components/common/Footer/Footer";
import AboutUs from "./pages/Static/AboutUs";
import PrivacyPolicy from "./pages/Static/PrivacyPolicy";
import TermsConditions from "./pages/Static/TermsConditions";

const Header = lazy(() => import("./components/common/Header/Header"));
// Imported directly, not lazily: it is the page most visits open first, so
// waiting for its own chunk only added a loading step to the first paint.

const OrganisationDashboard = lazy(
  () => import("./pages/Admin/OrganisationDashboard/OrganisationDashboard"),
);
const SageDashboard = lazy(
  () => import("./pages/Admin/SageDashboard/SageDashboard"),
);
const SubOrganisationPledgeData = lazy(
  () =>
    import(
      "./pages/Admin/OrganisationDashboard/SubOrganisationPledgeData"
    ),
);
const OtpLogin = lazy(() => import("./pages/Login/OtpLogin/OtpLogin"));
const ForgetPassword = lazy(
  () => import("./pages/Login/ForgetPassword/ForgetPassword"),
);
const ResetPassword = lazy(
  () => import("./pages/Login/ResetPassword/ResetPassword"),
);

const DashboardManagement = lazy(
  () => import("./pages/Admin/DashboardManagement/DashboardManagement"),
);

const AddDocument = lazy(() => import("./components/AddDocument/AddDocument"));

const UserManagement = lazy(
  () => import("./pages/Admin/UserManagement/UserManagement"),
);
const RoleManagement = lazy(
  () => import("./pages/Admin/RoleManagement/RoleManagement"),
);
const SideBarMenu = lazy(
  () => import("./components/common/SideBarMenu/SideBarMenu"),
);

const SageApplication = lazy(
  () => import("./pages/Admin/SageApplication/SageApplication"),
);
const SageApplicationReview = lazy(
  () =>
    import("./pages/Admin/SageApplication/SageApplicationReview/SageApplicationReview"),
);

const Event = lazy(() => import("./pages/Admin/Event/Event"));
const AddEvent = lazy(() => import("./pages/Admin/Event/AddEvent/AddEvent"));
const Volunteer = lazy(() => import("./pages/Admin/Volunteer/Volunteer"));
const VolunteerReview = lazy(
  () => import("./pages/Admin/Volunteer/VolunteerReview/VolunteerReview"),
);
const VayoSaathiReport = lazy(
  () => import("./pages/Admin/VayoSaathiReport/VayoSaathiReport"),
);
const VayoSaathiDetails = lazy(
  () =>
    import("./pages/Admin/VayoSaathiReport/VayoSaathiDetails/VayoSaathiDetails"),
);
const ActivityReview = lazy(
  () => import("./pages/Admin/VayoSaathiReport/ActivityReview/ActivityReview"),
);
const SageHome = lazy(() => import("./pages/Admin/SageHome/SageHome"));
const AddSageHome = lazy(
  () => import("./pages/Admin/SageHome/AddSageHome/AddSageHome"),
);
const AssistedDevice = lazy(
  () => import("./pages/Admin/AssistedDevice/AssistedDevice"),
);
const GeriatricCaregiver = lazy(
  () => import("./pages/Admin/GeriatricCaregiver/GeriatricCaregiver"),
);
const GeriatricCaregiverDetail = lazy(
  () =>
    import(
      "./pages/Admin/GeriatricCaregiver/GeriatricCaregiverDetail/GeriatricCaregiverDetail"
    ),
);
const AddAssistedDevice = lazy(
  () =>
    import("./pages/Admin/AssistedDevice/AddAssistedDevice/AddAssistedDevice"),
);

const Banner = lazy(() => import("./pages/Admin/Banner/Banner"));
const AddBanner = lazy(
  () => import("./pages/Admin/Banner/AddBanner/AddBanner"),
);
const QrManagement = lazy(
  () => import("./pages/Admin/QrManagement/QrManagement"),
);
const OfflinePledges = lazy(
  () => import("./pages/Admin/OfflinePledges/OfflinePledges"),
);
const AddQrCode = lazy(
  () => import("./pages/Admin/QrManagement/AddQrCode/AddQrCode"),
);
const Grievances = lazy(() => import("./pages/Admin/Grievances/Grievances"));
const FrontMenu = lazy(() => import("./pages/Admin/FrontMenu/FrontMenu"));
const AddFrontMenu = lazy(
  () => import("./pages/Admin/FrontMenu/AddFrontMenu/AddFrontMenu"),
);
const CmsPage = lazy(() => import("./pages/Admin/CmsPage/CmsPage"));
const AddCmsPage = lazy(
  () => import("./pages/Admin/CmsPage/AddCmsPage/AddCmsPage"),
);
const Story = lazy(() => import("./pages/Admin/Story/Story"));
const AddStory = lazy(() => import("./pages/Admin/Story/AddStory/AddStory"));
const Gallery = lazy(() => import("./pages/Admin/Gallery/Gallery"));
const AddGallery = lazy(
  () => import("./pages/Admin/Gallery/AddGallery/AddGallery"),
);
const Video = lazy(() => import("./pages/Admin/Video/Video"));
const AddVideo = lazy(() => import("./pages/Admin/Video/AddVideo/AddVideo"));
const Faq = lazy(() => import("./pages/Admin/Faq/Faq"));
const AddFaq = lazy(() => import("./pages/Admin/Faq/AddFaq/AddFaq"));
const Newsletter = lazy(() => import("./pages/Admin/Newsletter/Newsletter"));
const Gptm = lazy(() => import("./pages/Admin/Gptm/Gptm"));
const AddGptm = lazy(() => import("./pages/Admin/Gptm/AddGptm/AddGptm"));
const Documents = lazy(() => import("./pages/Admin/Documents/Documents"));
const SiteFeedback = lazy(
  () => import("./pages/Admin/SiteFeedback/SiteFeedback"),
);
const MenuManagement = lazy(
  () => import("./pages/Admin/MenuManagement/MenuManagement"),
);
const AddMenu = lazy(
  () => import("./pages/Admin/MenuManagement/AddMenu/AddMenu"),
);
const PledgeReport = lazy(
  () => import("./pages/Admin/PledgeReport/PledgeReport"),
);

// const PasswordPolicy = lazy(
//   () => import("./pages/Admin/PasswordPolicy/PasswordPolicy"),
// );

function App() {
  const { userToken, isAuthenticated, logout } = useAuth();
  const { showLoader, hideLoader } = useLoader();
  const { showToast } = useToast();
  const { setLogLevel, log } = useLogger();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Measure the real header height instead of hardcoding it. The header grew
  // taller with the accessibility bar + Bhashini widget, so fixed 128px/135px
  // offsets made it overlap the sidebar and content. Track the actual height so
  // the layout below always starts exactly under the header.
  //
  // This has to be a callback ref, not useRef + useLayoutEffect. <Header> is
  // lazy, so on a cold load it suspends and the header node does not exist when
  // a layout effect would run - the effect bailed out on a null ref and, with
  // deps of [isAuthenticated], never ran again. headerHeight stayed 0 for the
  // whole page load, so the sidebar and main content sat underneath the fixed
  // header and the first few menu items were invisible. A callback ref fires
  // exactly when the node mounts, whenever that turns out to be.
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerObserverRef = useRef<ResizeObserver | null>(null);

  const headerRef = useCallback((node: HTMLDivElement | null) => {
    headerObserverRef.current?.disconnect();
    headerObserverRef.current = null;
    if (!node) {
      setHeaderHeight(0);
      return;
    }
    const update = () => setHeaderHeight(node.offsetHeight);
    update();
    // Observing the node covers both the logged-out case (empty wrapper, height
    // 0) and later growth from fonts/widgets loading or the viewport resizing.
    const observer = new ResizeObserver(update);
    observer.observe(node);
    headerObserverRef.current = observer;
  }, []);

  // The wrapper div is not remounted when auth flips - only its children change
  // - so the callback ref above does not re-fire and the reset would depend
  // entirely on ResizeObserver catching the collapse to 0 on logout. Reset it
  // explicitly instead, so a stale offset can never push the login page down.
  useLayoutEffect(() => {
    if (!isAuthenticated) setHeaderHeight(0);
  }, [isAuthenticated]);

  useLayoutEffect(() => {
    return () => headerObserverRef.current?.disconnect();
  }, []);

  // Same problem, opposite edge: the footer is `position: fixed` at the
  // bottom of the viewport with a solid background, but the sidebar's and
  // main content's height were only ever `100vh - headerHeight` - nothing
  // accounted for the footer's own height. Its last ~50-60px (more on narrow
  // screens, where it wraps to multiple lines via flex-col) sat UNDER the
  // fixed footer, which then visually covered whatever sidebar/menu items
  // landed there - e.g. "Admin Menu" being the last item in an expanded
  // group, hidden behind the footer even though it was still in the DOM and
  // technically scrolled-to. Same dynamic-measurement fix as headerHeight,
  // for the same reason (the footer also wraps to a taller multi-line layout
  // on narrow screens, so a hardcoded height would be wrong there too).
  const [footerHeight, setFooterHeight] = useState(0);
  const footerObserverRef = useRef<ResizeObserver | null>(null);

  const footerRef = useCallback((node: HTMLElement | null) => {
    footerObserverRef.current?.disconnect();
    footerObserverRef.current = null;
    if (!node) {
      setFooterHeight(0);
      return;
    }
    const update = () => setFooterHeight(node.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    footerObserverRef.current = observer;
  }, []);

  useLayoutEffect(() => {
    if (!isAuthenticated) setFooterHeight(0);
  }, [isAuthenticated]);

  useLayoutEffect(() => {
    return () => footerObserverRef.current?.disconnect();
  }, []);

  // Mirror both measured heights onto the root element as CSS custom
  // properties, so components outside this tree - namely Header.tsx's mobile
  // sidebar drawer, which has no other way to know either height without
  // prop-drilling through several components - can read them too. The
  // mobile drawer had the exact same bug as the desktop sidebar (stretching
  // from top-0 to bottom-0 with no footer offset), just never noticed until
  // now since testing had focused on the desktop view.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--app-header-height",
      `${headerHeight}px`,
    );
  }, [headerHeight]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--app-footer-height",
      `${footerHeight}px`,
    );
  }, [footerHeight]);

  setupInterceptors(userToken, logout, showLoader, hideLoader, showToast, log);
  setLogLevel(LogLevel.INFO);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <div
        ref={headerRef}
        className={isAuthenticated ? "fixed top-0 left-0 right-0 z-50" : ""}
      >
        {isAuthenticated && (
          <Header
            onToggleSidebar={() =>
              setIsSidebarCollapsed((prevCollapsed) => !prevCollapsed)
            }
            sidebarCollapsed={isSidebarCollapsed}
          />
        )}
      </div>
      <div
        className="flex w-full"
        style={
          isAuthenticated
            ? {
                marginTop: `${headerHeight}px`,
                height: `calc(100vh - ${headerHeight}px)`,
              }
            : {}
        }
      >
        {isAuthenticated && (
          <aside
            className={`hidden md:block fixed left-0 overflow-y-auto sidebar-scroll border-r border-gray-300 bg-[#F9FAFB] ${
              isSidebarCollapsed ? "w-[64px]" : "w-[20%]"
            }`}
            style={{
              top: `${headerHeight}px`,
              height: `calc(100vh - ${headerHeight}px - ${footerHeight}px)`,
              transition: "width 420ms cubic-bezier(0.22, 1, 0.36, 1)",
              willChange: "width",
            }}
          >
            <SideBarMenu collapsed={isSidebarCollapsed} />
          </aside>
        )}
        <main
          id="main-content"
          tabIndex={-1}
          className={`w-full md:flex-1 overflow-y-auto !pb-7 ${
            isAuthenticated
              ? isSidebarCollapsed
                ? "md:ml-[64px]"
                : "md:ml-[20%]"
              : "w-full"
          }`}
          style={
            isAuthenticated
              ? {
                  height: `calc(100vh - ${headerHeight}px - ${footerHeight}px)`,
                  transition:
                    "margin-left 420ms cubic-bezier(0.22, 1, 0.36, 1)",
                  willChange: "margin-left",
                }
              : {}
          }
        >
          <Suspense fallback={<LoadingFallback />}>
            <RouteTitle />
            <Routes>
              <Route
                path="/"
                element={<PublicRoutes element={<Navigate to="/login" />} />}
              />
              <Route
                path="/login"
                element={<PublicRoutes element={<Login />} />}
              />

              <Route
                path="/otp-login"
                element={<PublicRoutes element={<OtpLogin />} />}
              />
              <Route
                path="/forget-password"
                element={<PublicRoutes element={<ForgetPassword />} />}
              />
              <Route
                path="/reset-password"
                element={<PublicRoutes element={<ResetPassword />} />}
              />
              <Route
                path="/dashboard"
                element={<PrivateRoutes element={<DashboardManagement />} />}
              />

              <Route
                path="/user-management"
                element={<PrivateRoutes element={<UserManagement />} />}
              />

              <Route
                path="/important-documents/add"
                element={<PrivateRoutes element={<AddDocument />} />}
              />

              <Route
                path="/role-management"
                element={<PrivateRoutes element={<RoleManagement />} />}
              />

              <Route
                path="/sage-applications"
                element={<PrivateRoutes element={<SageApplication />} />}
              />
              <Route
                path="/sage-applications/:id"
                element={<PrivateRoutes element={<SageApplicationReview />} />}
              />

              <Route
                path="/events"
                element={<PrivateRoutes element={<Event />} />}
              />
              <Route
                path="/events/add"
                element={<PrivateRoutes element={<AddEvent />} />}
              />
              <Route
                path="/events/edit/:id"
                element={<PrivateRoutes element={<AddEvent />} />}
              />

              <Route
                path="/volunteers"
                element={<PrivateRoutes element={<Volunteer />} />}
              />
              <Route
                path="/volunteers/:id"
                element={<PrivateRoutes element={<VolunteerReview />} />}
              />

              {/* Vayo Saathi Report - route_url on the m_menus row. The
                  sidebar entry comes from access_control, so only roles
                  granted the menu ever see these. */}
              <Route
                path="/vayo-sathi-report"
                element={<PrivateRoutes element={<VayoSaathiReport />} />}
              />
              <Route
                path="/vayo-sathi-report/vayo-sathi/:id"
                element={<PrivateRoutes element={<VayoSaathiDetails />} />}
              />
              <Route
                path="/vayo-sathi-report/activity/:id"
                element={<PrivateRoutes element={<ActivityReview />} />}
              />

              <Route
                path="/sage-homes"
                element={<PrivateRoutes element={<SageHome />} />}
              />
              <Route
                path="/sage-homes/add"
                element={<PrivateRoutes element={<AddSageHome />} />}
              />
              <Route
                path="/sage-homes/edit/:id"
                element={<PrivateRoutes element={<AddSageHome />} />}
              />

              <Route
                path="/assisted-devices"
                element={<PrivateRoutes element={<AssistedDevice />} />}
              />
              <Route
                path="/assisted-devices/add"
                element={<PrivateRoutes element={<AddAssistedDevice />} />}
              />
              <Route
                path="/assisted-devices/edit/:id"
                element={<PrivateRoutes element={<AddAssistedDevice />} />}
              />

              <Route
                path="/geriatric-caregivers"
                element={<PrivateRoutes element={<GeriatricCaregiver />} />}
              />
              <Route
                path="/geriatric-caregivers/:id"
                element={
                  <PrivateRoutes element={<GeriatricCaregiverDetail />} />
                }
              />
              {/* m_menus.route_url for this menu is /manage-geriatric-caregivers.
                  The sidebar rewrites it via resolveMenuRoute, but the stored
                  route is kept working so a direct hit does not 404. */}
              <Route
                path="/manage-geriatric-caregivers"
                element={<PrivateRoutes element={<GeriatricCaregiver />} />}
              />
              <Route
                path="/manage-geriatric-caregivers/:id"
                element={
                  <PrivateRoutes element={<GeriatricCaregiverDetail />} />
                }
              />

              {/* <Route
              path="/password-policy"
              element={<PrivateRoutes element={<PasswordPolicy />} />}
            /> */}

              <Route
                path="/banners"
                element={<PrivateRoutes element={<Banner />} />}
              />
              <Route
                path="/banners/add"
                element={<PrivateRoutes element={<AddBanner />} />}
              />
              <Route
                path="/banners/edit/:id"
                element={<PrivateRoutes element={<AddBanner />} />}
              />

              {/* Organisations role (12) landing page */}
              <Route
                path="/organisation-dashboard"
                element={<PrivateRoutes element={<OrganisationDashboard />} />}
              />
              {/* Department role (6) landing page - SAGE application counts.
                  Named distinctly from /dashboard so it can be granted without
                  also granting the internal admin dashboard. */}
              <Route
                path="/sage-dashboard"
                element={<PrivateRoutes element={<SageDashboard />} />}
              />
              {/* The sub-organisation report is now wanted in the navigation:
                  an organisation with branches (e.g. Birla, 162 schools) needs
                  its per-branch pledge counts, which the dashboard cannot show
                  because its table breaks down by organisation, not by branch. */}
              <Route
                path="/organisation-sub-organisations"
                element={<PrivateRoutes element={<SubOrganisationPledgeData />} />}
              />

              <Route
                path="/qr-management"
                element={<PrivateRoutes element={<QrManagement />} />}
              />
              {/* Super Admin only - the API enforces the same. */}
              <Route
                path="/offline-pledges"
                element={<PrivateRoutes element={<OfflinePledges />} requiredRole="Super Admin" />}
              />
              <Route
                path="/qr-management/add"
                element={<PrivateRoutes element={<AddQrCode />} />}
              />
              <Route
                path="/qr-management/edit/:id"
                element={<PrivateRoutes element={<AddQrCode />} />}
              />

              <Route
                path="/grievances"
                element={<PrivateRoutes element={<Grievances />} />}
              />

              <Route
                path="/front-menu"
                element={<PrivateRoutes element={<FrontMenu />} />}
              />
              <Route
                path="/front-menu/add"
                element={<PrivateRoutes element={<AddFrontMenu />} />}
              />
              <Route
                path="/front-menu/edit/:id"
                element={<PrivateRoutes element={<AddFrontMenu />} />}
              />

              <Route
                path="/cms-pages"
                element={<PrivateRoutes element={<CmsPage />} />}
              />
              <Route
                path="/cms-pages/add"
                element={<PrivateRoutes element={<AddCmsPage />} />}
              />
              <Route
                path="/cms-pages/edit/:id"
                element={<PrivateRoutes element={<AddCmsPage />} />}
              />

              <Route
                path="/stories"
                element={<PrivateRoutes element={<Story />} />}
              />
              <Route
                path="/stories/add"
                element={<PrivateRoutes element={<AddStory />} />}
              />
              <Route
                path="/stories/edit/:id"
                element={<PrivateRoutes element={<AddStory />} />}
              />

              <Route
                path="/gallery"
                element={<PrivateRoutes element={<Gallery />} />}
              />
              <Route
                path="/gallery/add"
                element={<PrivateRoutes element={<AddGallery />} />}
              />
              <Route
                path="/gallery/edit/:id"
                element={<PrivateRoutes element={<AddGallery />} />}
              />

              <Route
                path="/videos"
                element={<PrivateRoutes element={<Video />} />}
              />
              <Route
                path="/videos/add"
                element={<PrivateRoutes element={<AddVideo />} />}
              />
              <Route
                path="/videos/edit/:id"
                element={<PrivateRoutes element={<AddVideo />} />}
              />

              <Route
                path="/faqs"
                element={<PrivateRoutes element={<Faq />} />}
              />
              <Route
                path="/faqs/add"
                element={<PrivateRoutes element={<AddFaq />} />}
              />
              <Route
                path="/faqs/edit/:id"
                element={<PrivateRoutes element={<AddFaq />} />}
              />

              <Route
                path="/newsletter-subscribers"
                element={<PrivateRoutes element={<Newsletter />} />}
              />

              <Route
                path="/gptm-report"
                element={<PrivateRoutes element={<Gptm />} />}
              />
              <Route
                path="/gptm-report/add"
                element={<PrivateRoutes element={<AddGptm />} />}
              />
              <Route
                path="/gptm-report/edit/:id"
                element={<PrivateRoutes element={<AddGptm />} />}
              />

              <Route
                path="/important-documents"
                element={<PrivateRoutes element={<Documents />} />}
              />

              <Route
                path="/feedback"
                element={<PrivateRoutes element={<SiteFeedback />} />}
              />

              <Route
                path="/menu-management"
                element={<PrivateRoutes element={<MenuManagement />} />}
              />
              <Route
                path="/menu-management/add"
                element={<PrivateRoutes element={<AddMenu />} />}
              />
              <Route
                path="/menu-management/edit/:menu_id"
                element={<PrivateRoutes element={<AddMenu />} />}
              />

              <Route
                path="/pledge-reports"
                element={<PrivateRoutes element={<PledgeReport />} />}
              />

              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
          {isAuthenticated && <Footer footerRef={footerRef} />}
        </main>
      </div>
    </Suspense>
  );
}

export default App;
